import type {
  AvailabilitySchedule,
  AvailabilityScheduleInput,
  BlockTimeInput,
  BookingSettings,
  CalendarConnection,
  CalendarView,
  CreateInviteInput,
  EventType,
  EventTypeInput,
  EventTypeInputDraft,
  Holiday,
  HolidayCountry,
  HostBooking,
  HostBookingSummary,
  Invite,
  ManualBookingInput,
  PartialInput,
  PaymentsAccount,
  Transaction,
  UpdateCalendarConnectionInput,
} from '@bb/shared';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// --- Availability ---------------------------------------------------------

export const schedulesQuery = queryOptions({
  queryKey: ['booking', 'schedules'],
  queryFn: () => api<AvailabilitySchedule[]>('/v1/availability/schedules'),
});

export function useSchedules() {
  return useQuery(schedulesQuery);
}

export function useCreateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AvailabilityScheduleInput) =>
      api<AvailabilitySchedule>('/v1/availability/schedules', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: schedulesQuery.queryKey }),
  });
}

export function useUpdateSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: PartialInput<AvailabilityScheduleInput> & { id: string }) =>
      api<AvailabilitySchedule>(`/v1/availability/schedules/${id}`, {
        method: 'PATCH',
        body: input,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: schedulesQuery.queryKey }),
  });
}

export function useSetDefaultSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<void>(`/v1/availability/schedules/${id}/default`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: schedulesQuery.queryKey }),
  });
}

export function useDeleteSchedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/v1/availability/schedules/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: schedulesQuery.queryKey }),
  });
}

// --- Event types ----------------------------------------------------------

export const eventTypesQuery = queryOptions({
  queryKey: ['booking', 'eventTypes'],
  queryFn: () => api<EventType[]>('/v1/event-types'),
});

export function useEventTypes() {
  return useQuery(eventTypesQuery);
}

export function eventTypeQuery(id: string) {
  return queryOptions({
    queryKey: ['booking', 'eventTypes', id],
    queryFn: () => api<EventType>(`/v1/event-types/${id}`),
  });
}

export function useCreateEventType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: EventTypeInputDraft) =>
      api<EventType>('/v1/event-types', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: eventTypesQuery.queryKey }),
  });
}

export function useUpdateEventType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: PartialInput<EventTypeInput> & { id: string }) =>
      api<EventType>(`/v1/event-types/${id}`, { method: 'PATCH', body: input }),
    onSuccess: (data) => {
      qc.setQueryData(eventTypeQuery(data.id).queryKey, data);
      void qc.invalidateQueries({ queryKey: eventTypesQuery.queryKey });
    },
  });
}

export function useDuplicateEventType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<EventType>(`/v1/event-types/${id}/duplicate`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: eventTypesQuery.queryKey }),
  });
}

export function useDeleteEventType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/v1/event-types/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: eventTypesQuery.queryKey }),
  });
}

// --- Calendar connections -------------------------------------------------

export const connectionsQuery = queryOptions({
  queryKey: ['booking', 'connections'],
  queryFn: () => api<CalendarConnection[]>('/v1/calendar/connections'),
});

export function useConnections() {
  return useQuery(connectionsQuery);
}

export function useUpdateConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: UpdateCalendarConnectionInput & { id: string }) =>
      api<CalendarConnection>(`/v1/calendar/connections/${id}`, { method: 'PATCH', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: connectionsQuery.queryKey }),
  });
}

export function useResyncConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api<CalendarConnection>(`/v1/calendar/connections/${id}/resync`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: connectionsQuery.queryKey }),
  });
}

export function useDeleteConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/v1/calendar/connections/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: connectionsQuery.queryKey }),
  });
}

// --- Host bookings --------------------------------------------------------

export interface BookingListParams {
  view: 'upcoming' | 'past' | 'cancelled';
  eventTypeId?: string;
  q?: string;
  cursor?: string;
}

export function hostBookingsQuery(params: BookingListParams) {
  const qs = new URLSearchParams({ view: params.view });
  if (params.eventTypeId) qs.set('eventTypeId', params.eventTypeId);
  if (params.q) qs.set('q', params.q);
  if (params.cursor) qs.set('cursor', params.cursor);
  return queryOptions({
    queryKey: ['booking', 'bookings', params],
    queryFn: () =>
      api<{ items: HostBookingSummary[]; nextCursor: string | null }>(`/v1/bookings?${qs}`),
  });
}

export function hostBookingQuery(id: string) {
  return queryOptions({
    queryKey: ['booking', 'bookings', 'detail', id],
    queryFn: () => api<HostBooking>(`/v1/bookings/${id}`),
  });
}

function useBookingAction<TInput>(
  path: (input: TInput) => string,
  body?: (input: TInput) => unknown,
  /** Undoing an action is the same path with the opposite verb. */
  method: 'POST' | 'DELETE' = 'POST',
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) =>
      api<HostBooking | undefined>(path(input), {
        method,
        ...(body ? { body: body(input) } : {}),
      }),
    onSuccess: (data) => {
      if (data) qc.setQueryData(hostBookingQuery(data.id).queryKey, data);
      void qc.invalidateQueries({ queryKey: ['booking', 'bookings'] });
    },
  });
}

/**
 * A span of time rather than a page of rows, for the calendar.
 *
 * Keyed on the exact window, so moving to next week is a new query and moving
 * back shows the old answer at once while it refreshes.
 */
export function useCalendar(params: {
  from: string;
  to: string;
  agentId?: string;
  eventTypeId?: string;
}) {
  const qs = new URLSearchParams({ from: params.from, to: params.to });
  if (params.agentId) qs.set('agentId', params.agentId);
  if (params.eventTypeId) qs.set('eventTypeId', params.eventTypeId);
  return useQuery({
    queryKey: ['booking', 'bookings', 'calendar', qs.toString()],
    queryFn: () => api<CalendarView>(`/v1/bookings/calendar?${qs}`),
    // A week that is already drawn should not blink while it refreshes.
    placeholderData: (prev) => prev,
  });
}

/** Putting back an hour or a day that was taken out. */
export function useUnblockTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BlockTimeInput) =>
      api<AvailabilitySchedule>('/v1/availability/unblock', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['booking', 'schedules'] });
      void qc.invalidateQueries({ queryKey: ['booking', 'bookings'] });
    },
  });
}

/** Taking an hour, a morning or a day out of your own week. */
export function useBlockTime() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: BlockTimeInput) =>
      api<AvailabilitySchedule>('/v1/availability/block', { method: 'POST', body: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['booking', 'schedules'] });
      // The calendar draws its own availability from bookings, but a block
      // changes what the public page offers, so anything cached about slots is
      // now wrong.
      void qc.invalidateQueries({ queryKey: ['booking', 'bookings'] });
    },
  });
}

export function useReassignBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, agentId }: { id: string; agentId: string }) =>
      api<HostBooking>(`/v1/bookings/${id}/reassign`, { method: 'POST', body: { agentId } }),
    /*
      The same two steps every other booking action takes.

      This used to invalidate `['bookings']`, which nothing is stored under —
      the booking being looked at lives at `['booking', 'bookings', 'detail',
      id]`. So the server handed the appointment to somebody else and the page
      went on showing the cached answer, which read as the change reverting.
    */
    onSuccess: (data) => {
      qc.setQueryData(hostBookingQuery(data.id).queryKey, data);
      void qc.invalidateQueries({ queryKey: ['booking', 'bookings'] });
    },
  });
}

export function useCancelHostBooking() {
  return useBookingAction<{ id: string; message?: string; refund?: 'full' | 'none' }>(
    (i) => `/v1/bookings/${i.id}/cancel`,
    (i) => ({
      ...(i.message ? { message: i.message } : {}),
      ...(i.refund ? { refund: i.refund } : {}),
    }),
  );
}

export function useRescheduleHostBooking() {
  return useBookingAction<{ id: string; start: string }>(
    (i) => `/v1/bookings/${i.id}/reschedule`,
    (i) => ({ start: i.start }),
  );
}

export function useMarkNoShow() {
  return useBookingAction<{ id: string }>((i) => `/v1/bookings/${i.id}/no-show`);
}

export function useUnmarkNoShow() {
  return useBookingAction<{ id: string }>(
    (i) => `/v1/bookings/${i.id}/no-show`,
    undefined,
    'DELETE',
  );
}

export function useResendConfirmation() {
  return useBookingAction<{ id: string }>((i) => `/v1/bookings/${i.id}/resend-confirmation`);
}

export function useRecreateCalendarEvent() {
  return useBookingAction<{ id: string }>((i) => `/v1/bookings/${i.id}/recreate-calendar-event`);
}

export function useSetHostNotes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; hostNotes: string }) =>
      api<HostBooking>(`/v1/bookings/${input.id}/notes`, {
        method: 'PATCH',
        body: { hostNotes: input.hostNotes },
      }),
    onSuccess: (data) => qc.setQueryData(hostBookingQuery(data.id).queryKey, data),
  });
}

export function useCreateManualBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ManualBookingInput) =>
      api<HostBooking>('/v1/bookings', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['booking', 'bookings'] }),
  });
}

// --- Payments (Stripe Connect) --------------------------------------------

export const paymentsAccountQuery = queryOptions({
  queryKey: ['booking', 'payments', 'account'],
  queryFn: () => api<PaymentsAccount>('/v1/payments/account'),
});

export function usePaymentsAccount() {
  return useQuery(paymentsAccountQuery);
}

export function useRefreshPayments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<PaymentsAccount>('/v1/payments/refresh', { method: 'POST' }),
    onSuccess: (data) => {
      qc.setQueryData(paymentsAccountQuery.queryKey, data);
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useDisconnectPayments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/v1/payments/connect', { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: paymentsAccountQuery.queryKey });
      void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });
}

export function useTransactions(enabled = true) {
  return useQuery({
    queryKey: ['booking', 'payments', 'transactions'],
    queryFn: () =>
      api<{
        items: Transaction[];
        nextCursor: string | null;
        totals: { paid: number; refunded: number; currency: string | null };
      }>('/v1/payments/transactions'),
    enabled,
  });
}

export function useRefundBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; amount?: number }) =>
      api<{ amount: number }>(`/v1/bookings/${input.id}/refund`, {
        method: 'POST',
        body: input.amount ? { amount: input.amount } : {},
      }),
    onSuccess: (_data, input) => {
      void qc.invalidateQueries({ queryKey: hostBookingQuery(input.id).queryKey });
      void qc.invalidateQueries({ queryKey: ['booking', 'payments', 'transactions'] });
    },
  });
}

// --- Holidays -------------------------------------------------------------

export function useHolidays(country: HolidayCountry, from: string, to: string, enabled = true) {
  return useQuery({
    queryKey: ['booking', 'holidays', country, from, to],
    queryFn: () => api<Holiday[]>(`/v1/holidays?country=${country}&from=${from}&to=${to}`),
    staleTime: 24 * 60 * 60_000,
    enabled,
  });
}

// --- Invites ---------------------------------------------------------------

export const invitesQuery = (eventTypeId: string) =>
  queryOptions({
    queryKey: ['booking', 'invites', eventTypeId],
    queryFn: () => api<{ items: Invite[] }>(`/v1/invites?eventTypeId=${eventTypeId}`),
  });

export function useCreateInvite(eventTypeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInviteInput) =>
      api<Invite>('/v1/invites', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: invitesQuery(eventTypeId).queryKey }),
  });
}

export function useRevokeInvite(eventTypeId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<Invite>(`/v1/invites/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: invitesQuery(eventTypeId).queryKey }),
  });
}

// --- Settings -------------------------------------------------------------

export const bookingSettingsQuery = queryOptions({
  queryKey: ['booking', 'settings'],
  queryFn: () => api<BookingSettings>('/v1/booking-settings'),
});

export function useUpdateBookingSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PartialInput<BookingSettings>) =>
      api<BookingSettings>('/v1/booking-settings', { method: 'PATCH', body: input }),
    onSuccess: (data) => qc.setQueryData(bookingSettingsQuery.queryKey, data),
  });
}
